import {debug} from '@/shared/helpers/debug';
import {handleBack} from '@/shared/composables/useBackHandler';
import {App} from '@capacitor/app';
import {Browser} from '@capacitor/browser';
import {computed, watchEffect, type Plugin} from 'vue';
import {useRouter} from 'vue-router';
import {getAvailableLanguages} from '@/stores/localeStore';
import {CapacitorUpdater} from '@capgo/capacitor-updater';
import {useUserStore} from '@/stores/userStore';

/**
 * Configures handling of hybrid app specific global functionality:
 * - Handling of OIDC authentication redirects
 * - Handling of universal/app links (https deep links)
 * - Hardware back button: closes overlays before navigating back
 *
 * Supported URL formats:
 * - Custom scheme OIDC: {appId}://login?code=...
 */
export const hybridAppPlugin = {
    install(app) {
        if (!__IS_HYBRID_APP__) {
            return;
        }
        app.runWithContext(() => {
            const router = useRouter();

            App.addListener('backButton', () => {
                if (handleBack()) {
                    return;
                }

                if (router.options.history.state?.back) {
                    router.back();
                }
            });

            const userId = computed(() => useUserStore().user?.id ?? '');

            watchEffect(() => {
                CapacitorUpdater.setCustomId({customId: userId.value});
            });

            App.addListener('appUrlOpen', async data => {
                debug.log('App opened with URL:', data.url);

                if (data.url.includes('://login')) {
                    await Browser.close();
                    return handleOidcAuthenticationRedirect(data.url);
                }

                if (data.url.startsWith('https://')) {
                    handleUniversalLink(data.url);
                }
            });

            function handleOidcAuthenticationRedirect(url: string) {
                const parsedUrl = new URL(url);
                const queryString = parsedUrl.search;

                debug.log('Redirecting to callback with query string:', queryString);

                const resolved = router.resolve({name: 'CallBack'});
                router.push(resolved.path + queryString);
            }

            function handleUniversalLink(url: string) {
                const parsedUrl = new URL(url);
                const pathname = parsedUrl.pathname;

                // Strip locale prefix (e.g. /de/, /en/) if present
                const localePattern = getAvailableLanguages().join('|');
                const strippedPath = pathname.replace(
                    new RegExp(`^\\/(${localePattern})(?=\\/)`),
                    ''
                );
                const destination = strippedPath + parsedUrl.search + parsedUrl.hash;

                const resolved = router.resolve(destination);
                if (!resolved.matched.length || resolved.name === 'NotFound') {
                    debug.log('Universal link did not match any route, ignoring:', destination);
                    return;
                }

                debug.log('Navigating to universal link path:', destination);
                router.push(resolved);
            }
        });
    },
} satisfies Plugin;
